export const ReleaseStatus = Object.freeze({
  Generating: 1,
  GenerationFailed: 2,
  Draft: 3,
  Published: 4,
  Archived: 5,
});

export const ReleaseStatusMeta = {
  [ReleaseStatus.Generating]: { label: 'Generating…', color: 'info', icon: 'mdi-loading mdi-spin' },
  [ReleaseStatus.GenerationFailed]: {
    label: 'Generation failed',
    color: 'error',
    icon: 'mdi-alert-circle-outline',
  },
  [ReleaseStatus.Draft]: { label: 'Draft', color: 'grey', icon: 'mdi-file-document-edit-outline' },
  [ReleaseStatus.Published]: {
    label: 'Published',
    color: 'success',
    icon: 'mdi-check-circle-outline',
  },
  [ReleaseStatus.Archived]: {
    label: 'Archived',
    color: 'grey lighten-1',
    icon: 'mdi-archive-outline',
  },
};

export const ReleaseItemCategory = Object.freeze({
  Other: 0,
  Feature: 1,
  Improvement: 2,
  Fix: 3,
  Security: 4,
  Breaking: 5,
});

export const ReleaseItemCategoryLabels = {
  [ReleaseItemCategory.Feature]: 'Feature',
  [ReleaseItemCategory.Improvement]: 'Improvement',
  [ReleaseItemCategory.Fix]: 'Fix',
  [ReleaseItemCategory.Security]: 'Security',
  [ReleaseItemCategory.Breaking]: 'Breaking',
  [ReleaseItemCategory.Other]: 'Other',
};

/**
 * Icon + Vuetify colour per category, so a category reads the same in the picker, in the
 * read-only cell and on the suggested-category chip. Colours are Vuetify names so they
 * follow the active theme.
 */
export const ReleaseItemCategoryMeta = {
  [ReleaseItemCategory.Feature]: { icon: 'mdi-star-outline', color: 'amber darken-2' },
  [ReleaseItemCategory.Improvement]: {
    icon: 'mdi-arrow-up-bold-circle-outline',
    color: 'teal',
  },
  [ReleaseItemCategory.Fix]: { icon: 'mdi-wrench-outline', color: 'info' },
  [ReleaseItemCategory.Security]: { icon: 'mdi-shield-lock-outline', color: 'deep-purple' },
  [ReleaseItemCategory.Breaking]: { icon: 'mdi-alert-octagon-outline', color: 'error' },
  [ReleaseItemCategory.Other]: { icon: 'mdi-dots-horizontal-circle-outline', color: 'grey' },
};

/**
 * @param {number} category ReleaseItemCategory value
 * @returns {{ icon: string, color: string }} falls back to the "Other" presentation
 */
export function releaseItemCategoryMeta(category) {
  return ReleaseItemCategoryMeta[category] || ReleaseItemCategoryMeta[ReleaseItemCategory.Other];
}

export const ReleaseRecipientPolicy = Object.freeze({
  CompanyOwners: 1,
  CompanyAdmins: 2,
  AllActiveUsers: 3,
});

export const ReleaseRecipientPolicyLabels = {
  [ReleaseRecipientPolicy.CompanyOwners]: 'Company owners only',
  [ReleaseRecipientPolicy.CompanyAdmins]: 'Company owners + admins',
  [ReleaseRecipientPolicy.AllActiveUsers]: 'All active users',
};

/** Mirrors the API's ReleaseReminderRecipientMode. */
export const ReleaseReminderRecipientMode = Object.freeze({
  ConfiguredEmails: 1,
  PublishPermissionHolders: 2,
});

/**
 * Mirrors the API's ReleaseDraftKind. A release carries two independent note streams: the customer
 * text that is published and emailed, and the internal text (Jira keys + links) that never leaves
 * the management screens.
 */
export const ReleaseDraftKind = Object.freeze({
  Customer: 1,
  Internal: 2,
});

export const ReleaseDeliveryStatus = Object.freeze({
  Pending: 1,
  Sent: 2,
  Failed: 3,
  Suppressed: 4,
});

export const ReleaseDeliveryStatusMeta = {
  [ReleaseDeliveryStatus.Pending]: { label: 'Pending', color: 'info' },
  [ReleaseDeliveryStatus.Sent]: { label: 'Sent', color: 'success' },
  [ReleaseDeliveryStatus.Failed]: { label: 'Failed', color: 'error' },
  [ReleaseDeliveryStatus.Suppressed]: { label: 'Suppressed', color: 'grey' },
};

export const ReleaseReminderStatus = Object.freeze({
  Scheduled: 1,
  Sending: 2,
  Sent: 3,
  Cancelled: 4,
  Completed: 5,
  Failed: 6,
});

export const ReleaseReminderStatusMeta = {
  [ReleaseReminderStatus.Scheduled]: { label: 'Scheduled', color: 'info' },
  [ReleaseReminderStatus.Sending]: { label: 'Sending', color: 'info' },
  [ReleaseReminderStatus.Sent]: { label: 'Sent', color: 'warning' },
  [ReleaseReminderStatus.Cancelled]: { label: 'Cancelled', color: 'grey' },
  [ReleaseReminderStatus.Completed]: { label: 'Completed', color: 'success' },
  [ReleaseReminderStatus.Failed]: { label: 'Failed', color: 'error' },
};

export const ReleasePublicationStatus = Object.freeze({
  Queued: 1,
  Sending: 2,
  Completed: 3,
  CompletedWithFailures: 4,
  NoEmail: 5,
});

export const ReleasePublicationStatusMeta = {
  [ReleasePublicationStatus.Queued]: { label: 'Queued', color: 'info' },
  [ReleasePublicationStatus.Sending]: { label: 'Sending', color: 'info' },
  [ReleasePublicationStatus.Completed]: { label: 'Completed', color: 'success' },
  [ReleasePublicationStatus.CompletedWithFailures]: {
    label: 'Completed with failures',
    color: 'warning',
  },
  [ReleasePublicationStatus.NoEmail]: { label: 'No email sent', color: 'grey' },
};

export const RELEASE_ERROR_MESSAGES = {
  RELEASE_NOT_FOUND: 'This release no longer exists.',
  RELEASE_ALREADY_EXISTS: 'A release already exists for this Jira version.',
  RELEASE_NO_ISSUES_SELECTED: 'Select at least one ticket before generating.',
  RELEASE_DRAFT_CONFLICT:
    'Someone else edited this draft. Review the latest version before saving.',
  RELEASE_DRAFT_TOO_LARGE: 'The notes are empty or too long to save.',
  RELEASE_AUDIENCE_EMPTY: 'No eligible recipients were found for the selected companies.',
  RELEASE_JIRA_UNAVAILABLE: 'Could not reach Jira. Try again shortly.',
  RELEASE_NAME_LOCKED: 'The release name can’t change after it has been published.',
  RELEASE_REMINDER_NO_RECIPIENTS: 'No reminder recipients are configured — set some in Settings.',
};

/**
 * Pulls the server-supplied message out of a rejected axios error.
 * The API returns `ErrorResponseMessage.Message` holding codes such as `RELEASE_DRAFT_CONFLICT`;
 * casing of the JSON property is not guaranteed, so both spellings are probed.
 * Falls back to the axios error message (e.g. a network failure) when the body carries nothing.
 * @param {*} err rejected axios error
 * @returns {string} raw code / message, or an empty string
 */
export function releaseErrorCode(err) {
  const data = err && err.response ? err.response.data : null;
  const candidates = [
    data && data.message,
    data && data.Message,
    data && data.error,
    data && data.Error,
    err && err.message,
  ];
  const found = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return found ? found.trim() : '';
}

/**
 * Maps a rejected axios error onto a user-facing message.
 * Known `RELEASE_*` codes map through RELEASE_ERROR_MESSAGES; an unknown but human-readable
 * server message is surfaced as-is; anything else (an all-caps code we do not know, or only a
 * generic axios message) yields the caller's fallback.
 * @param {*} err rejected axios error
 * @param {string} [fallback]
 * @returns {string}
 */
export function releaseErrorMessage(err, fallback = 'Something went wrong.') {
  const code = releaseErrorCode(err);
  if (code && Object.prototype.hasOwnProperty.call(RELEASE_ERROR_MESSAGES, code)) {
    return RELEASE_ERROR_MESSAGES[code];
  }

  // Only trust the response body for raw pass-through — axios' own `err.message`
  // ("Request failed with status code 500") is not something to show a user.
  const data = err && err.response ? err.response.data : null;
  const serverText = [data && data.message, data && data.Message].find(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
  if (serverText) {
    const text = serverText.trim();
    const isUnknownCode = /^[A-Z0-9_]+$/.test(text);
    if (!isUnknownCode) return text;
  }

  return fallback;
}

/**
 * True when a request failed because the server-side `ReleaseNotes:Enabled` flag is off.
 * Every gated Release Notes endpoint 404s while the feature is disabled, so a 404 on a
 * screen's FIRST load is treated as "feature not enabled" rather than a hard error.
 * Shared by the Vuex module (FETCH_RELEASES latch) and by individual screens.
 * @param {*} err rejected axios error
 * @returns {boolean}
 */
export function isFeatureDisabledError(err) {
  return !!(err && err.response && err.response.status === 404);
}
